/** @format */

import BreadCrumbs from '@/components/shared/BreadCrumbsPDP';
import { ContactsBooking } from '@/components/shared/ContactsBooking/ContactsBooking';
import { PromoBanner } from '@/components/shared/PromoBanner';
import { ShowOnMobile } from '@/components/ui/ShowOnMobile';
import { GTMsetup } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { Tenant, TRoom } from '@/ducks/room/declarations';
import { utilityGetRoomInfo } from '@/ducks/room/helpers';
import { TLocale } from '@/utils/gatsby/types';
import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useAppTranslation } from '../../../../../i18n/declarations';
import calculateListingUrl from '../../../../../utils/gatsby/calculate-listing-url';
import { CITIES_TO_COUNTRY } from '../../../../shared/CitySelectionDropdown/Content.constants';
import { PromoBannerContainer, TextPromoBanner } from '../../../../shared/PromoBanner/PromoBanner.style';
import { RoomInfoTitle } from '../../index.style';
import BookCard from '../BookCard';
import RoomDescription from '../PageDescription';
import RoomAssetNotes from '../RoomAssetNotes';
import RoomColiversInfo from '../RoomColiversInfo/RoomColiversInfo';
import RoomInfoPosition from '../RoomInfoPosition';
import RoomInfoPricePromo from '../RoomInfoPricePromo';
import RoomPremiumServices from '../RoomPremiumServices';
import RoomWhatYouWillFind from '../RoomWhatYouWillFind';
import { RoomInfoCode, RoomInfoContainer, RoomInfoDescriptionArea, RoomInfoHeader } from './RoomInfo.style';

const RoomInfo: FC<RoomInfoProps> = ({ locale, room }) => {
  const isBrowser = typeof window !== 'undefined';
  const currentPage = isBrowser ? decodeURI(window.location.pathname) : '';
  const { intl } = useAppTranslation();
  const roomInfoRef = useRef<HTMLDivElement>(null);
  const { title, description } = utilityGetRoomInfo(locale, room);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);
  const listingUrl = calculateListingUrl(room?.city.nameEn, intl, 'all', '');

  const displayNoDepositBanner = useMemo(() => {
    if (room?.commercialConditions?.depositAmount === 0) {
      return true;
    }
    return false;
  }, [room?.commercialConditions?.depositAmount]);

  const orderedTenants = useMemo(
    () => [...(room?.tenants || [])].sort((a, b) => parseInt(a.roomNumber, 10) - parseInt(b.roomNumber, 10)),
    [room?.tenants],
  );

  const numberOfTenants = useMemo(() => room?.tenants.filter(t => t.type === 'coliver').length, [room?.tenants]);

  useEffect(() => {
    const country =
      CITIES_TO_COUNTRY.get(room.city.nameEn.toLowerCase().charAt(0).toUpperCase() + room.city.nameEn.slice(1)) ?? '';
    GTMsetup(country, locale, 'detail_page');
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (isBrowser) {
      setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (isBrowser) {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  return (
    <RoomInfoHeader ref={roomInfoRef}>
      <RoomInfoContainer>
        {!!listingUrl && <BreadCrumbs currentPage={currentPage} listingUrl={listingUrl} language={locale} />}
        {windowWidth && windowWidth <= 768 && (
          <>
            <RoomInfoTitle>{title}</RoomInfoTitle>
            <RoomInfoPosition room={room} />
          </>
        )}
        <RoomInfoCode>REF CODE: {room.code}</RoomInfoCode>
        {displayNoDepositBanner && (
          <PromoBannerContainer $page="pdp" $layout="mobile">
            <TextPromoBanner>{intl.formatMessage({ id: 'pdp.promo_no_deposit' })}</TextPromoBanner>
          </PromoBannerContainer>
        )}
        <PromoBanner
          page="pdp"
          layout="mobile"
          promoBannerInfo={{
            buildingId: room.building.id || null,
            bedCode: room.code,
            flatId: room.flat.id,
            cityId: room.city.id,
          }}
        />
        <RoomInfoPricePromo room={room} />
        {!!description && (
          <RoomInfoDescriptionArea>
            <RoomDescription>{description}</RoomDescription>
          </RoomInfoDescriptionArea>
        )}
        <RoomAssetNotes assetNotes={room.assetNotes} />
        <ShowOnMobile>
          <ContactsBooking room={room as TRoom} isMobile />
        </ShowOnMobile>
        <RoomPremiumServices />
        <RoomWhatYouWillFind coliversCount={numberOfTenants} room={room} />
        {orderedTenants && orderedTenants.length > 0 && (
          <RoomColiversInfo room={room as TRoom} rooms={orderedTenants} />
        )}
      </RoomInfoContainer>
      <BookCard room={room} lite locale={locale} />
    </RoomInfoHeader>
  );
};

export default memo(RoomInfo);

type RoomInfoProps = {
  locale: TLocale;
  room: TRoom;
};
